import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, update, get, onValue } from 'firebase/database';
import { logAdminAction } from '../../firebase/auditLogger';
import { Link } from 'react-router-dom';
import './AdminSecurityReview.css';

function AdminSecurityReview() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanLogs, setScanLogs] = useState([]);
  const [telemetry, setTelemetry] = useState([
    { type: 'success', text: '[OK] SINGULARITY PROTOCOL v2.7.0.7 ACTIVE' },
    { type: 'info', text: '[STREAM] ESTABLISHING REALTIME SYNC...' }
  ]);

  // Terminal Metadata
  const [meta, setMeta] = useState({
    uptime: "00:00:00",
    load: "8.4%",
    kernel: "SINGULARITY-V2.7.0"
  });

  // 1. Clock & Load Simulation
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setMeta(prev => ({
        ...prev,
        uptime: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`,
        load: (Math.random() * 2 + 8).toFixed(1) + "%"
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 2. REALTIME INCIDENT LISTENER
  useEffect(() => {
    const incidentsRef = ref(db, 'security_flags');
    const unsubscribe = onValue(incidentsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
        setIncidents(list);
        setTelemetry(prev => [
          { type: 'success', text: `[SYNC] GLOBAL THREAT GRID UPDATED: ${list.length} NODES` },
          ...prev.slice(0, 15)
        ]);
      } else {
        setIncidents([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 3. Dossier Resolution (Context-Aware)
  useEffect(() => {
    if (selectedId) {
      const incident = incidents.find(r => r.id === selectedId);
      if (incident) {
        const userRef = ref(db, `users/${incident.targetUser}`);
        get(userRef).then(snap => {
          if (snap.exists()) setSelectedUser(snap.val());
          else setSelectedUser({ uid: incident.targetUser, name: "Unknown Entity", role: "Unindexed" });
        });
      }
    } else {
      setSelectedUser(null);
    }
  }, [selectedId, incidents]);

  // Telemetry Helper
  const addLog = (text, type = 'info') => {
    setTelemetry(prev => [{ type, text: `[${new Date().toLocaleTimeString()}] ${text}` }, ...prev.slice(0, 15)]);
  };

  const runAudit = async () => {
    setIsScanning(true);
    setScanProgress(0);
    setScanLogs([]);
    addLog(">>> INITIALIZING SINGULARITY CORE AUDIT...", 'info');
    
    const steps = [
      { t: "SYNCHRONIZING KERNEL L0...", p: 20 },
      { t: "SCANNING /USERS ENTROPY...", p: 40 },
      { t: "MAPPING THREAT VECTORS...", p: 60 },
      { t: "VALIDATING RSA SIGNATURES...", p: 80 },
      { t: "AUDIT COMPLETE. READY.", p: 100 }
    ];

    for (const step of steps) {
      await new Promise(r => setTimeout(r, 800));
      addLog(step.t, step.p === 100 ? 'success' : 'info');
      setScanProgress(step.p);
    }
    setIsScanning(false);
  };

  const handleProtocol = async (action) => {
    if (!selectedId) return;
    try {
      if (action === 'NULLIFY') {
        await update(ref(db, `security_flags/${selectedId}`), { status: 'resolved', resolvedAt: Date.now() });
        addLog(`PROTOCOL: NULLIFY EXECUTED ON ${selectedId}`, 'success');
      } else if (action === 'QUARANTINE') {
        await update(ref(db, `users/${selectedUser.uid}`), { status: 'suspended' });
        await update(ref(db, `security_flags/${selectedId}`), { status: 'resolved', resolution: 'quarantined' });
        addLog(`PROTOCOL: QUARANTINE EXECUTED ON ID: ${selectedUser.uid}`, 'alert');
      }
      setSelectedId(null);
      logAdminAction(selectedId, `Executed Protocol: ${action}`, 'security_protocol');
    } catch (err) {
      addLog(`PROTOCOL ERROR: ${err.message}`, 'alert');
    }
  };

  if (loading) return (
    <div className="security-review-portal">
      <div style={{margin:'auto', color:'#00ffae', fontSize:'1.2rem', fontFamily:'Fira Code'}}>
        [ESTABLISHING SECURE CONNECTION...]
      </div>
    </div>
  );

  return (
    <div className="security-review-portal">
      <div className="singularity-bg"></div>
      <div className="singularity-grid-overlay"></div>
      <div className="crt-overlay"></div>

      <div className={`singularity-container ${selectedId ? 'dossier-active' : ''}`}>
        {/* LEFT: DIAGNOSSTICS */}
        <div className="singularity-left">
          <div className="diagnostic-header">
            <h2>System Diagnostics</h2>
          </div>

          {selectedId && (
            <div className="target-lock-indicator" style={{background: 'rgba(0, 255, 174, 0.1)', border: '1px solid #00ffae', padding: '10px', borderRadius: '8px', fontSize: '0.65rem', color: '#00ffae', fontWeight: 'bold', animation: 'blink 1s infinite'}}>
               <i className="fa-solid fa-crosshairs"></i> TARGET LOCKED: {selectedId}
            </div>
          )}
          
          <div className="radar-vessel">
            <div className="radar-sweep"></div>
            <div className="radar-node" style={{top:'20%', left:'30%'}}></div>
            <div className="radar-node" style={{top:'60%', left:'70%'}}></div>
            <div className="radar-node" style={{top:'40%', left:'45%'}}></div>
          </div>

          <div className="diagnostic-matrix">
            <div className="diag-item">
              <span className="diag-label">KERNEL VERSION</span>
              <span className="diag-value">{meta.kernel}</span>
            </div>
            <div className="diag-item">
              <span className="diag-label">CORE LOAD</span>
              <span className="diag-value" style={{color: parseFloat(meta.load) > 10 ? '#ff375f' : '#fff'}}>{meta.load}</span>
            </div>
            <div className="diag-item">
              <span className="diag-label">SYSTEM UPTIME</span>
              <span className="diag-value">{meta.uptime}</span>
            </div>
          </div>

          <div className="telemetry-scratchpad">
            {telemetry.map((log, i) => (
              <div key={i} className={`telemetry-line ${log.type}`}>{log.text}</div>
            ))}
          </div>

          <button className={`protocol-btn success ${isScanning ? 'disabled' : ''}`} onClick={runAudit} disabled={isScanning}>
             <i className="fa-solid fa-atom"></i> {isScanning ? 'SCANNING...' : 'INIT CORE AUDIT'}
          </button>
        </div>

        {/* MIDDLE: INCIDENT QUEUE */}
        <div className="singularity-middle">
          <div className="main-header">
            <div>
              <h1>Singularity Incident Queue</h1>
              <p style={{color: '#8b949e', margin:0}}>Direct administrative oversight of Origin Core threats.</p>
            </div>
            <div style={{textAlign: 'right'}}>
              <div style={{fontSize: '0.8rem', color: '#ff375f', fontWeight: 'bold'}}>
                CRITICAL {incidents.filter(i => i.risk === 'Critical' && i.status !== 'resolved').length}
              </div>
              <div style={{fontSize: '0.8rem', color: '#8b949e'}}>
                PENDING {incidents.filter(i => i.status !== 'resolved').length}
              </div>
            </div>
          </div>

          <div className="incident-grid">
            {incidents.filter(i => i.status !== 'resolved').map(incident => (
              <div 
                key={incident.id} 
                className={`singularity-card ${selectedId === incident.id ? 'active' : ''}`}
                onClick={() => setSelectedId(incident.id)}
              >
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px'}}>
                  <span style={{fontSize:'0.7rem', color:'#00ffae', fontWeight:'bold'}}>{incident.id}</span>
                  <span className={`status-badge ${incident.risk?.toLowerCase()}`}>{incident.risk}</span>
                </div>
                <h3 className="card-title">{incident.type || 'Anomalous Activity'}</h3>
                <div style={{fontSize:'0.75rem', color:'#8b949e', marginTop:'5px'}}>
                  {new Date(incident.timestamp).toLocaleTimeString()} • {incident.targetUser}
                </div>
                <div className="action-required">
                  <span>ACTION REQUIRED</span>
                  <i className="fa-solid fa-chevron-right"></i>
                </div>
              </div>
            ))}
            {incidents.filter(i => i.status !== 'resolved').length === 0 && (
              <div style={{gridColumn:'1/-1', textAlign:'center', padding:'100px', color:'#8b949e'}}>
                <i className="fa-solid fa-circle-check" style={{fontSize:'3rem', color:'#00ffae', marginBottom:'20px', display:'block'}}></i>
                <p>ALL VECTORS CLEAR. NO ACTIVE THREATS DETECTED.</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: DOSSIER SIDEBAR */}
        {selectedId && (
          <div className="singularity-right">
            <div className="dossier-header">
              <button 
                className="close-btn" 
                onClick={() => { setSelectedId(null); setSelectedUser(null); }} 
                style={{position:'absolute', top:'20px', left:'20px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', padding:'8px 12px', borderRadius:'8px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'bold'}}
              >
                <i className="fa-solid fa-xmark"></i> CLOSE
              </button>
              <div className={`dossier-avatar ${!selectedUser ? 'syncing' : ''}`}>
                <i className={`fa-solid ${!selectedUser ? 'fa-sync fa-spin' : 'fa-user-shield'}`}></i>
              </div>
              <h2 style={{margin:0}}>{selectedUser?.name || 'Synchronizing...'}</h2>
              <p style={{color:'#00ffae', fontSize:'0.8rem', fontStyle:'italic', opacity: selectedUser ? 1 : 0.5}}>
                {selectedUser ? (selectedUser.role || 'Unindexed Role') : 'Accessing Kernel Data...'}
              </p>
            </div>

            <div className="dossier-body">
              <div className="intel-section">
                <h4>IDENTITY DNA</h4>
                <div className="dna-meter"><div className="dna-fill" style={{width: selectedUser ? '85%' : '0%'}}></div></div>
                <p style={{fontSize:'0.7rem', color:'#8b949e', marginTop:'8px'}}>RELIABILITY SCORE: 85% • SOURCE: BIOMETRICS</p>
              </div>

              <div className="intel-section">
                <h4>SECURITY OVERVIEW</h4>
                <div className="intel-grid-compact">
                  <div className="intel-item-mini"><span>UID</span><strong>{selectedUser?.uid || '---'}</strong></div>
                  <div className="intel-item-mini"><span>STATUS</span><strong style={{color:'#00ffae'}}>{selectedUser?.status || 'ACTIVE'}</strong></div>
                  <div className="intel-item-mini"><span>BREACHES</span><strong>0</strong></div>
                  <div className="intel-item-mini"><span>TRUST</span><strong>L3</strong></div>
                </div>
              </div>
            </div>

            <div className="protocol-actions">
              <button 
                className="protocol-btn success" 
                onClick={() => handleProtocol('NULLIFY')}
              >
                <i className="fa-solid fa-check"></i> PROTOCOL: NULLIFY
              </button>
              <button 
                className="protocol-btn alert" 
                onClick={() => handleProtocol('QUARANTINE')}
              >
                <i className="fa-solid fa-ban"></i> PROTOCOL: QUARANTINE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSecurityReview;
